import { useEffect, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { isLanguageSelectable } from "../i18n/languages";
import { resolveQrDeployment } from "../qr/resolveDeployment";
import { useSession } from "../state/SessionProvider";

/**
 * Opaque QR entry. Loads minimal deployment context into React memory only.
 * Never shows partner labels, placement, city, or trafficking framing.
 * No analytics / POST / GPS.
 */
export function QrEntryPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const {
    translate,
    setQrContext,
    setLanguageCode,
    clearHelpAnswers,
    clearConcernAnswers,
    clearHospitalityAnswers,
    setResourceCategories,
    setHelpBookmark,
    setConcernBookmark,
    setHospitalityBookmark,
  } = useSession();
  const applied = useRef(false);
  const result = resolveQrDeployment(code);

  useEffect(() => {
    if (applied.current || !result.ok) return;
    applied.current = true;

    // Fresh QR entry: clear prior flow answers; do not carry old bookmarks.
    clearHelpAnswers();
    clearConcernAnswers();
    clearHospitalityAnswers();
    setResourceCategories(null);
    setHelpBookmark(null);
    setConcernBookmark(null);
    setHospitalityBookmark(null);

    setQrContext(result.session);

    const lang = result.deployment.defaultLanguage;
    if (lang && isLanguageSelectable(lang)) {
      setLanguageCode(lang);
    }

    // Always land on neutral /. Suggested mode is offered on Home — never forced.
    navigate("/", { replace: true });
  }, [
    result,
    navigate,
    setQrContext,
    setLanguageCode,
    clearHelpAnswers,
    clearConcernAnswers,
    clearHospitalityAnswers,
    setResourceCategories,
    setHelpBookmark,
    setConcernBookmark,
    setHospitalityBookmark,
  ]);

  if (result.ok) {
    return (
      <section className="stack" aria-live="polite">
        <h1 tabIndex={-1}>{translate("brand.name")}</h1>
        <p className="muted">{translate("page.qr.loading")}</p>
      </section>
    );
  }

  return (
    <section className="stack" aria-labelledby="qr-unavailable">
      <h1 id="qr-unavailable" tabIndex={-1}>
        {translate("page.qr.unknown")}
      </h1>
      <p className="muted">{translate("page.qr.unknown_body")}</p>
      <Link className="mode-link mode-link--quiet" to="/">
        {translate("page.qr.continue")}
      </Link>
    </section>
  );
}
